import React, { useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Typography, IconButton, Tooltip } from '@material-ui/core'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import Icon from '../../../../../components/atoms/Icon'
import InlineAbundanceRenderer from '../../Chat/InlineAbundanceRenderer'
import { parseCodeBlock, isAbundanceBlock } from '../../Chat/abundanceDetect'

const useStyles = makeStyles((theme) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff'
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(3)
  },
  markdown: {
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    color: theme.palette.text.primary,
    '& h1': {
      fontSize: '1.75rem',
      fontWeight: 600,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
      '&:first-child': { marginTop: 0 }
    },
    '& h2': {
      fontSize: '1.5rem',
      fontWeight: 600,
      marginTop: theme.spacing(2.5),
      marginBottom: theme.spacing(1.5)
    },
    '& h3': {
      fontSize: '1.25rem',
      fontWeight: 600,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1)
    },
    '& p': {
      marginBottom: theme.spacing(2),
      '&:last-child': { marginBottom: 0 }
    },
    '& ul, & ol': {
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(3)
    },
    '& li': { marginBottom: theme.spacing(0.5) },
    '& code': {
      backgroundColor: theme.palette.grey[100],
      padding: '2px 6px',
      borderRadius: theme.shape.borderRadius,
      fontSize: '0.875rem',
      fontFamily: '"SF Mono", Monaco, monospace'
    },
    '& pre': {
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      overflow: 'auto',
      marginBottom: theme.spacing(2),
      '& code': { backgroundColor: 'transparent', padding: 0 }
    },
    '& blockquote': {
      borderLeft: `3px solid ${theme.palette.primary.main}`,
      paddingLeft: theme.spacing(2),
      marginLeft: 0,
      marginBottom: theme.spacing(2),
      fontStyle: 'italic',
      color: theme.palette.text.secondary
    },
    '& hr': {
      margin: theme.spacing(3, 0),
      border: 0,
      borderTop: `1px solid ${theme.palette.divider}`
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginBottom: theme.spacing(2)
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1),
      textAlign: 'left'
    },
    '& th': {
      backgroundColor: theme.palette.grey[50],
      fontWeight: 600
    }
  }
}))

const markdownComponents = {
  pre ({ children, node }) {
    const { language, codeString } = parseCodeBlock(children, node)

    if (isAbundanceBlock(language, codeString)) {
      return <InlineAbundanceRenderer code={codeString} />
    }

    return <pre>{children}</pre>
  }
}

export default function MarkdownViewer ({ data, metadata, onRegisterActions }) {
  const classes = useStyles()
  const markdownRef = useRef(null)

  const handleCopyFormatted = useCallback(async () => {
    if (!markdownRef.current) return
    try {
      const html = markdownRef.current.innerHTML
      const plainText = markdownRef.current.innerText
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' })
        })
      ])
    } catch (err) {
      console.error('Failed to copy formatted content:', err)
      if (markdownRef.current) {
        navigator.clipboard.writeText(markdownRef.current.innerText)
      }
    }
  }, [])

  const handleCopyRaw = useCallback(() => {
    navigator.clipboard.writeText(data)
      .catch(err => console.error('Failed to copy raw markdown:', err))
  }, [data])

  useEffect(() => {
    if (!onRegisterActions) return
    onRegisterActions(
      <>
        <Tooltip title='Copy raw' placement='bottom'>
          <IconButton size='small' onClick={handleCopyRaw}>
            <Icon name='code' customSize='16px' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Copy' placement='bottom'>
          <IconButton size='small' onClick={handleCopyFormatted}>
            <Icon name='copy' customSize='16px' />
          </IconButton>
        </Tooltip>
      </>
    )
    return () => onRegisterActions(null)
  }, [onRegisterActions, handleCopyRaw, handleCopyFormatted])

  if (!data || typeof data !== 'string') {
    return (
      <Box className={classes.content}>
        <Typography color='error'>Invalid markdown data</Typography>
      </Box>
    )
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.content}>
        <div ref={markdownRef} className={classes.markdown}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {data}
          </ReactMarkdown>
        </div>
      </Box>
    </Box>
  )
}

MarkdownViewer.propTypes = {
  data: PropTypes.string.isRequired,
  metadata: PropTypes.shape({
    title: PropTypes.string,
    contentType: PropTypes.string
  }),
  onRegisterActions: PropTypes.func
}
